import * as React from 'react';
import { updateSurvey, setSurveyLanguage, deleteSurveyTranslation, createSurveyTranslation } from '../../../api/surveyClientApi';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { FormField } from '../../../designSystem/formField/FormField';
import { StartEndDateFields } from '../../../designSystem/inputs/StartEndDateFields';
import { updateActivity } from '../../../api/activityClientApi';
import { Activity, surveyHelper } from '../../../amorg-common';
import { Survey } from '../../../amorg-common';
import { AmorgMenu, AmorgMenuContent } from '../../../designSystem/amorgMenu';
import { LanguageCode } from '../../../amorg-common';
import { Label } from '../../../designSystem/Label';
import { MessageCard } from '../../../designSystem/Message';

type SurveyInfoPanelProps = {
    activity:Activity,
    survey:Survey,
    selectedLanguageCode:LanguageCode,
    disableEdit:boolean,
    onSelectedLanguageCodeChanged:(code:LanguageCode)=>void,
    onSurveyUpdated:()=>void,
    onActivityUpdated:()=>void
}
export function SurveyInfoPanel(props:SurveyInfoPanelProps):JSX.Element
{
    const [dateError, setDateError] = React.useState(false);

    async function handleSaveDates(start: Date, end: Date) {
        const updateResult = await updateSurvey(
            props.survey.id, props.survey.title, start, end
        );
        if (!updateResult.isSuccess)
            setDateError(true);
        else {
            setDateError(false);
            props.onSurveyUpdated();
        }
    }

    async function handleSaveActivityDates(start: Date, end: Date) {
        const updateResult = await updateActivity(
            props.activity.id,
            props.activity.title,
            props.activity.description,
            start,
            end,
            props.activity.location
        );
        if(!updateResult.isSuccess)
            setDateError(true);
        if (updateResult.isSuccess){
            setDateError(false);
            props.onActivityUpdated();
        }
    }

    function getLanguageMenuItems(survey:Survey):AmorgMenuContent[] 
    {
        const supportedLanguages = surveyHelper.getValidLanguageCodes(survey);

        function getMenuItem(code:LanguageCode, languageName:string):AmorgMenuContent
        {
            const isSupported = supportedLanguages.find(l => l === code) !== undefined;

            let popupMenuContent:AmorgMenuContent[] = [];
            if(!props.disableEdit){

                if(isSupported){
                    // Default: supported, set as standard language
                    popupMenuContent = [];

                    // Supported, but not standard
                    if(survey.languageCode !== code){
                        popupMenuContent = [
                            {
                                type:'leadingContent',
                                leadingContent: {type:'icon', iconName:'star_starOutlined'},
                                primaryContent: { type:'text', text:`Vælg som standard`},
                                styleVariant:'default',
                                onClick: async () => {
                                    await setSurveyLanguage(survey.id, code);
                                    props.onSurveyUpdated();
                                }
                            },
                            {
                                type:'leadingContent',
                                leadingContent: {type:'icon', iconName:'interface_actions_delete'},
                                primaryContent: { type:'text', text:`Fjern sprogversion`},
                                styleVariant:'default',
                                onClick: async () => {
                                    if(code === props.selectedLanguageCode){
                                        const newCode = supportedLanguages.find(sl => sl !== props.selectedLanguageCode);
                                        props.onSelectedLanguageCodeChanged(newCode);
                                    }
                                    await deleteSurveyTranslation(survey.id, code);
                                    props.onSurveyUpdated();
                                }
                            }
                        ];
                    }
                }
                // Not supported
                else{
                    popupMenuContent = [
                        {
                            type:'leadingContent',
                            leadingContent: {type:'icon', iconName:'actions_plus'},
                            primaryContent: { type:'text', text:`Tilføj sprogversion`},
                            styleVariant:'default',
                            onClick: async () => {
                                await createSurveyTranslation(survey.id, code);
                                props.onSurveyUpdated();
                            }
                        }
                    ];
                }

            }

            return {
                type:'leadingContent',
                leadingContent: props.selectedLanguageCode === code 
                    ? {type:'icon', iconName:'actions_done'}
                    : {type:'none'},
                primaryContent: survey.languageCode === code 
                    ? { type:'text', text:`${languageName} (standard)`}
                    : { type:'text', text:`${languageName}`},
                styleVariant:isSupported ? 'default' : 'strike-through',
                onClick:isSupported ? ()=>props.onSelectedLanguageCodeChanged(code) : undefined,
                popupMenu:popupMenuContent.length === 0 
                    ? undefined
                    : { content:popupMenuContent }
            }
        }

        return [ getMenuItem('da', 'Dansk'), getMenuItem('en', 'Engelsk')];
    }

    return (
        <Flex flexDirection="row" gap={16} justifyContent="space-between">
            <Flex flexDirection="column" gap={16}>
                <Flex flexDirection="row" gap={16} justifyContent="space-between">
                    <FormField 
                        label={"Besvarelsesperiode"} 
                        inputComponent={
                            <StartEndDateFields 
                                start={props.survey.start} end={props.survey.end} 
                                onChanged={handleSaveDates} />
                        } />

                    <FormField 
                        label={"APV-periode"} 
                        inputComponent={
                            <StartEndDateFields 
                                start={props.activity.start} end={props.activity.end} 
                                onChanged={handleSaveActivityDates} />
                        } />
                </Flex>
                
                {dateError &&
                        <MessageCard variant="error">Du har valgt en ugyldig kombination af datoer. Besvarelsesperioden for spørgeskemaet skal ligge indenfor den valgte APV-periode.</MessageCard>
                }

            </Flex>

            <Flex flexDirection="column" gap={4}>
                <Label>Sprogunderstøttelse</Label>
                <AmorgMenu content={getLanguageMenuItems(props.survey)} />
            </Flex>
            
        </Flex>
    );
}